import React, { useEffect, useRef } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./viewFranchiseManger.css";
import { viewSiteVisit, viewHomeVisit } from "../../Redux/Actions/franchiseManagerAction";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import HierarchyChain from "./hierarchyChain";

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return "—";
    }
};

const humanize = (v) =>
    v ? String(v).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : v;

const money = (v) => (v || v === 0 ? `₹${v}` : v);

const statusClass = (status) =>
    status === "completed" ? "approved" : ["cancelled", "declined"].includes(status) ? "rejected" : "pending";

/* ── Dummy/default coords to filter out (Google HQ & SF defaults) ── */
const VFM_DUMMY_COORDS = [
    [37.421998333333335, -122.084],
    [37.785834, -122.406417],
];

const isDummyCoord = (lat, lng) =>
    VFM_DUMMY_COORDS.some(
        ([dlat, dlng]) => Math.abs(lat - dlat) < 0.0001 && Math.abs(lng - dlng) < 0.0001
    );

/* ── Leaflet map rendered inside a card (loaded via CDN, no npm dep) ── */
const AgentRouteMap = ({ agentLocations, dropLocation }) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);

    useEffect(() => {
        if (!document.getElementById("leaflet-css")) {
            const link = document.createElement("link");
            link.id = "leaflet-css";
            link.rel = "stylesheet";
            link.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
            document.head.appendChild(link);
        }

        const initMap = () => {
            if (mapInstanceRef.current || !mapRef.current) return;
            const L = window.L;

            const validAgentPoints = (agentLocations || [])
                .filter(
                    (p) =>
                        typeof p?.lat === "number" &&
                        typeof p?.lng === "number" &&
                        !isDummyCoord(p.lat, p.lng)
                )
                .map((p) => [p.lat, p.lng]);

            const startLatLng = validAgentPoints[0] || null;

            // location is GeoJSON format: coordinates = [lng, lat]
            const dropLatLng =
                dropLocation?.coordinates?.length === 2
                    ? [dropLocation.coordinates[1], dropLocation.coordinates[0]]
                    : null;

            if (!startLatLng && !dropLatLng) return;

            const allPoints = [
                ...(startLatLng ? [startLatLng] : []),
                ...validAgentPoints,
                ...(dropLatLng ? [dropLatLng] : []),
            ];

            const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false });
            mapInstanceRef.current = map;

            L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                attribution: "© OpenStreetMap contributors",
                maxZoom: 19,
            }).addTo(map);

            // ── Start marker (green) ──
            if (startLatLng) {
                const startIcon = L.divIcon({
                    html: `<div class="vfm-map-marker vfm-map-marker--start">
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                       <circle cx="12" cy="12" r="10"/>
                     </svg>
                   </div>`,
                    className: "",
                    iconSize: [32, 32],
                    iconAnchor: [16, 16],
                });
                L.marker(startLatLng, { icon: startIcon })
                    .addTo(map)
                    .bindPopup("<b>Start Location</b>");
            }

            // ── Agent trail polyline ──
            if (validAgentPoints.length > 1) {
                L.polyline(validAgentPoints, {
                    color: "#0D152B",
                    weight: 3,
                    opacity: 0.75,
                    dashArray: "6 4",
                }).addTo(map);
            }

            // ── Agent location dots (numbered) ──
            validAgentPoints.forEach((point, idx) => {
                const isLast = idx === validAgentPoints.length - 1;
                const dotIcon = L.divIcon({
                    html: `<div class="vfm-map-dot-numbered ${isLast ? "vfm-map-dot-numbered--last" : ""}">
                     <span class="vfm-map-dot-number">${idx + 1}</span>
                   </div>`,
                    className: "",
                    iconSize: [22, 22],
                    iconAnchor: [11, 11],
                });
                L.marker(point, { icon: dotIcon })
                    .addTo(map)
                    .bindPopup(isLast ? `<b>Last Known Position</b><br/>Point ${idx + 1}` : `Point ${idx + 1}`);
            });

            // ── Drop location marker (orange pin) ──
            if (dropLatLng) {
                const dropIcon = L.divIcon({
                    html: `<div class="vfm-map-marker vfm-map-marker--drop">
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                       <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                       <circle cx="12" cy="10" r="3" fill="white"/>
                     </svg>
                   </div>`,
                    className: "",
                    iconSize: [32, 32],
                    iconAnchor: [16, 16],
                });
                L.marker(dropLatLng, { icon: dropIcon })
                    .addTo(map)
                    .bindPopup(
                        `<b>Drop Location</b><br/><span style="font-size:11px;color:#666">${dropLatLng[0].toFixed(6)}, ${dropLatLng[1].toFixed(6)}</span>`
                    );
            }

            // ── Fit bounds ──
            if (allPoints.length > 1) {
                map.fitBounds(L.latLngBounds(allPoints), { padding: [40, 40] });
            } else {
                map.setView(allPoints[0], 16);
            }
        };

        if (window.L) {
            initMap();
        } else {
            const script = document.createElement("script");
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
            script.onload = initMap;
            document.head.appendChild(script);
        }

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [agentLocations, dropLocation]);

    return <div ref={mapRef} className="vfm-map-container" />;
};

const hasValidCoords = (loc) =>
    loc && typeof loc.lat === "number" && typeof loc.lng === "number";

const ViewVisit = ({ type }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isSite = type === "site";

    const { siteVisitDetail, homeVisitDetail, visitDetailLoading = false } = useSelector(
        (state) => state.franchiseManager || {}
    );
    const data = isSite ? siteVisitDetail : homeVisitDetail;

    useEffect(() => {
        if (id) {
            dispatch(isSite ? viewSiteVisit(id) : viewHomeVisit(id)).catch(() => {
                toast.error("Failed to load details");
            });
        }
    }, [id, isSite, dispatch]);

    if (visitDetailLoading && !data) {
        return (
            <MainLayout>
                <div className="vfm-wrapper">
                    <div className="vfm-loading">
                        <div className="vfm-spinner-lg" />
                        <span>Loading details...</span>
                    </div>
                </div>
            </MainLayout>
        );
    }

    if (!visitDetailLoading && !data) {
        return (
            <MainLayout>
                <div className="vfm-wrapper">
                    <div className="vfm-empty-state">
                        <h3>Record not found</h3>
                        <button className="vfm-btn vfm-btn--primary" type="button" onClick={() => navigate(-1)}>
                            Back to list
                        </button>
                    </div>
                </div>
            </MainLayout>
        );
    }

    const h = data.hierarchy || {};
    const nodes = [
        { label: "Franchise Manager", name: h.franchise?.name },
        { label: "City Head", name: h.city_head?.name },
        { label: "Locality Head", name: h.locality_head?.name },
        { label: "Partner", name: h.agent?.name },
        { label: "Lead", name: h.lead?.name, active: true },
    ];

    const people = [
        ["Franchise Manager", h.franchise],
        ["City Head", h.city_head],
        ["Locality Head", h.locality_head],
        ["Partner", h.agent],
        ["Lead", h.lead],
    ];

    const franchiseId = h.franchise?._id;
    const cityHeadId = h.city_head?._id;
    const localityHeadId = h.locality_head?._id;

    const profileLink = {
        "Franchise Manager": franchiseId && `/franchise/view/${franchiseId}`,
        "City Head": cityHeadId && (franchiseId
            ? `/franchise/${franchiseId}/city-heads/${cityHeadId}/view`
            : `/franchise/view/${cityHeadId}`),
        "Locality Head": localityHeadId && (franchiseId && cityHeadId
            ? `/franchise/${franchiseId}/city-heads/${cityHeadId}/locality-heads/${localityHeadId}/view`
            : `/franchise/view/${localityHeadId}`),
        Partner: h.agent?._id && `/franchise/partner/view/${h.agent._id}`,
    };

    const info = isSite
        ? [
            ["Project", data.project?.project_name],
            ["Visit By", humanize(data.site_visit_by)],
            ["Visit Date", formatDate(data.site_visit_date_time)],
            ["Guests", data.no_of_guest],
            ["Purpose", humanize(data.site_visit_purpose)],
            ["Ride Type", humanize(data.ride_type)],
            ["Round Trip", data.is_round_trip ? "Yes" : "No"],
            ["Distance", data.distance ? `${data.distance} km` : data.distance],
            ["Estimated Price", money(data.estimated_price)],
            ["Follow Up", humanize(data.follow_up)],
            ["Pickup Address", data.pickup_address, true],
            ["Drop Address", data.drop_address, true],
        ]
        : [
            ["Business Type", humanize(data.business_type)],
            ["Appointment Date", formatDate(data.appointment_date)],
            ["Time Slot", data.time_slot],
            ["Appointment For", humanize(data.appointment_for)],
            ["Booking Amount", money(data.booking_amount)],
            ["Payment Status", humanize(data.payment_status)],
            ["Follow Up", humanize(data.follow_up)],
            ["Cancel Reason (User)", data.cancel_reason_by_user],
            ["Cancel Reason (Partner)", data.cancel_reason_by_agent],
        ];

    const projects = isSite ? (data.project ? [data.project] : []) : data.projects || [];
    const cities = isSite ? [] : data.cities?.length ? data.cities : data.city ? [data.city] : [];

    // Home Visit only — agent route map
    const routeTrail = !isSite
        ? (Array.isArray(data?.agent_location) ? data.agent_location : [])
            .filter((p) => hasValidCoords(p) && !isDummyCoord(p.lat, p.lng))
            .slice()
            .sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at))
        : [];

    const routeStart = routeTrail[0] || null;
    const routeLast = routeTrail[routeTrail.length - 1] || null;

    const dropLocation =
        !isSite && Array.isArray(data?.location?.coordinates) && data.location.coordinates.length === 2
            ? { lat: data.location.coordinates[1], lng: data.location.coordinates[0] }
            : null;

    const showRouteMap = !isSite && (routeTrail.length > 0 || hasValidCoords(dropLocation));

    return (
        <MainLayout>
            <div className="vfm-wrapper">
                <div className="vfm-header">
                    <div className="vfm-header-left">
                        <button className="vfm-back-btn" onClick={() => navigate(-1)} type="button" aria-label="Go back">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="vfm-title">{isSite ? "Site Visit" : "Home Visit"} Details</h1>
                            <p className="vfm-subtitle">Lead, partner and franchise hierarchy</p>
                        </div>
                    </div>
                    <span className={`vfm-doc-status vfm-doc-status--${statusClass(data.status)}`}>{data.status || "—"}</span>
                </div>

                <HierarchyChain nodes={nodes} />

                <div className="vfm-section-card">
                    <div className="vfm-section-header">
                        <span className="vfm-section-badge">01</span>
                        <h3 className="vfm-section-title">{isSite ? "Site Visit" : "Home Visit"} Information</h3>
                    </div>
                    <div className="vfm-detail-grid">
                        {info.map(([label, value, full]) => (
                            <div className={`vfm-detail-item ${full ? "vfm-detail-item--full" : ""}`} key={label}>
                                <span className="vfm-detail-label">{label}</span>
                                <span className="vfm-detail-value">{value || value === 0 ? value : "—"}</span>
                            </div>
                        ))}
                        <div className="vfm-detail-item">
                            <span className="vfm-detail-label">Created On</span>
                            <span className="vfm-detail-value">{formatDate(data.created_at)}</span>
                        </div>
                    </div>
                </div>

                <div className="vfm-section-card">
                    <div className="vfm-section-header">
                        <span className="vfm-section-badge">02</span>
                        <h3 className="vfm-section-title">People</h3>
                    </div>
                    <div className="vfm-detail-grid">
                        <div className="vfm-people-grid">
                            {people.map(([label, p]) => (
                                <div className="vfm-person-card" key={label}>
                                    <div className="vfm-person-avatar">
                                        {(p?.name || "—").slice(0, 2).toUpperCase()}
                                    </div>
                                    <div className="vfm-person-info">
                                        <span className="vfm-detail-label">{label}</span>
                                        <span className="vfm-detail-value">{p?.name || "—"}</span>
                                        <span className="vfm-person-sub">
                                            {[p?.email, p?.contact_number].filter(Boolean).join(" · ") || ""}
                                        </span>
                                    </div>
                                    {profileLink[label] && (
                                        <Link to={profileLink[label]} className="vfm-history-btn vfm-person-btn">
                                            View
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                                <polyline points="12 5 19 12 12 19" />
                                            </svg>
                                        </Link>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {(projects.length > 0 || cities.length > 0) && (
                    <div className="vfm-section-card">
                        <div className="vfm-section-header">
                            <span className="vfm-section-badge">03</span>
                            <h3 className="vfm-section-title">Location</h3>
                        </div>
                        {cities.length > 0 && (
                            <div className="vfm-tag-group">
                                <span className="vfm-tag-group-label">City</span>
                                <div className="vfm-tag-list">
                                    {cities.map((c) => (
                                        <span className="vfm-tag vfm-tag--city" key={c._id}>{c.city_name}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {projects.length > 0 && (
                            <div className="vfm-tag-group">
                                <span className="vfm-tag-group-label">Project</span>
                                <div className="vfm-tag-list">
                                    {projects.map((p) => (
                                        <span className="vfm-tag vfm-tag--project" key={p._id}>{p.project_name}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {showRouteMap && (
                    <div className="vfm-section-card">
                        <div className="vfm-section-header">
                            <span className="vfm-section-badge">04</span>
                            <h3 className="vfm-section-title">Agent Route Map</h3>
                        </div>

                        {/* Legend */}
                        <div className="vfm-map-legend">
                            <div className="vfm-map-legend-item">
                                <span className="vfm-map-legend-dot vfm-map-legend-dot--start" />
                                Start Location
                            </div>
                            <div className="vfm-map-legend-item">
                                <span className="vfm-map-legend-dot vfm-map-legend-dot--trail" />
                                Agent Trail
                            </div>
                            <div className="vfm-map-legend-item">
                                <span className="vfm-map-legend-dot vfm-map-legend-dot--last" />
                                Last Position
                            </div>
                            {hasValidCoords(dropLocation) && (
                                <div className="vfm-map-legend-item">
                                    <span className="vfm-map-legend-dot vfm-map-legend-dot--drop" />
                                    Drop Location
                                </div>
                            )}
                        </div>

                        <AgentRouteMap
                            agentLocations={data?.agent_location}
                            dropLocation={data?.location}
                        />

                        {/* Coords summary */}
                        <div className="vfm-map-coords">
                            {routeStart && (
                                <div className="vfm-map-coord-item">
                                    <span className="vfm-map-coord-label">Start</span>
                                    <span className="vfm-map-coord-val">
                                        {routeStart.lat.toFixed(6)}, {routeStart.lng.toFixed(6)}
                                    </span>
                                </div>
                            )}
                            {hasValidCoords(dropLocation) && (
                                <div className="vfm-map-coord-item">
                                    <span className="vfm-map-coord-label">Drop</span>
                                    <span className="vfm-map-coord-val">
                                        {dropLocation.lat.toFixed(6)}, {dropLocation.lng.toFixed(6)}
                                    </span>
                                </div>
                            )}
                            {routeLast && (
                                <div className="vfm-map-coord-item">
                                    <span className="vfm-map-coord-label">Last</span>
                                    <span className="vfm-map-coord-val">
                                        {routeLast.lat.toFixed(6)}, {routeLast.lng.toFixed(6)}
                                    </span>
                                </div>
                            )}
                            <div className="vfm-map-coord-item">
                                <span className="vfm-map-coord-label">Points</span>
                                <span className="vfm-map-coord-val">{routeTrail.length} tracked</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default ViewVisit;